"use client";

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { useRouter } from "next/navigation";
import { clientAPI, tryRefresh } from "@/lib/client-fetch";
import { trackAction } from "@/lib/track-action";
import { isTokenExpiredOrExpiringSoon } from "@/lib/auth/cookies";

export type User = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_calii_plus_member: boolean;
  num_remaining_calii_plus_coupons: number | null;
  cellphone: string | null;
  account_balance: number | null;
  city: string | null;
  microhub_id: string | null;
  shipping_info_id: number | null;
  full_address: string | null;
};

export type RegisterData = {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  cellphone?: string;
};

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ ok: boolean; user?: User; error?: string; status?: number }>;
  register: (data: RegisterData) => Promise<{ ok: boolean; user?: User; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
  initialUser: User | null;
};

export function AuthProvider({ children, initialUser }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(initialUser);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const refreshUser = useCallback(async (): Promise<User | null> => {
    const result = await clientAPI.get<User>("/api/auth/me");
    if (result.ok && result.data) {
      setUser(result.data);
      return result.data;
    } else {
      setUser(null);
      return null;
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    const result = await clientAPI.post("/api/auth/login", { email, password });
    if (result.ok) {
      const user = await refreshUser();
      setIsLoading(false);
      router.refresh();
      return { ok: true, user: user ?? undefined };
    } else {
      setIsLoading(false);
      return { ok: false, error: result.error || "Credenciales inválidas", status: result.status };
    }
  }, [refreshUser, router]);

  const register = useCallback(async (data: RegisterData) => {
    setIsLoading(true);
    const result = await clientAPI.post("/api/auth/register", data as unknown as Record<string, unknown>);
    if (result.ok) {
      const user = await refreshUser();
      setIsLoading(false);
      router.refresh();
      return { ok: true, user: user ?? undefined };
    } else {
      setIsLoading(false);
      return { ok: false, error: result.error || "Error al crear cuenta" };
    }
  }, [refreshUser, router]);

  // Proactively refresh the token on mount and whenever the user returns to the tab.
  // This ensures endpoints that don't require auth but return personalized data
  // (e.g. /collections) always receive a valid token.
  useEffect(() => {
    const maybeRefresh = () => {
      if (isTokenExpiredOrExpiringSoon()) tryRefresh()
    }
    maybeRefresh()
    document.addEventListener("visibilitychange", maybeRefresh)
    return () => document.removeEventListener("visibilitychange", maybeRefresh)
  }, [])

  const logout = useCallback(async () => {
    setIsLoading(true);
    trackAction("LOGOUT");
    await clientAPI.post("/api/auth/logout");
    setUser(null);
    setIsLoading(false);
    router.refresh();
    router.push("/");
  }, [router]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
